/**
 * The "Show Me" affordance: a button that opens SchedulerConnectionDiagram in a large modal.
 *
 * Two exports, because two things want it:
 *   - SchedulerConnectionDiagramDialog — the modal alone, controlled, for any caller that already
 *     owns an open flag (a menu item, a help link).
 *   - ShowMeSchedulerDiagramButton — button plus modal, self-contained. This is what the
 *     Schedulers and Vaults screens drop into their header.
 *
 * ---------------------------------------------------------------------------------------------
 * WHY THE BODY IS WIDTH-CAPPED BY VIEWPORT HEIGHT
 *
 * The diagram is a fixed 1920x1080 canvas scaled to whatever width its container reports, so its
 * height is width / 1.778 — width is the only lever, and it decides both legibility and whether
 * the modal scrolls. The transport controls (play/pause, Back/Next) render BELOW the canvas, so a
 * canvas taller than the modal pushes its own controls out of view: the user is left with a
 * diagram they cannot drive without scrolling past it.
 *
 * So the body is capped at the width whose canvas still fits the remaining height:
 *
 *     max-width = max(720px, (92vh - 150px) * 1.7778)
 *
 * 150px is the chrome above and below the canvas. MEASURED in the browser, not estimated: header
 * 76 + controls row 29 + gap 12 + body padding-bottom 24 = 141, plus 9px of slack for a header
 * title that wraps in a longer locale. The 720px floor is deliberate: below 720 of *container*
 * width the diagram switches to its vertical stacked layout, which is ~2.5 screens tall, and a
 * short-but-wide window (say 1200x520) would otherwise be handed the tall layout — much worse
 * than a wide layout that scrolls slightly. Width still yields to the parent, so a genuinely
 * narrow window gets the vertical layout as intended.
 *
 * What this costs, stated plainly: on a 1280x800 laptop the canvas lands at scale 0.54 and the
 * diagram's caption renders at ~13px. 16:9 is unforgiving — a canvas wide enough for a 20px
 * caption needs 900px of height, which an 800px window does not have. The alternative was to fill
 * the modal's full width and let the body scroll, which pushes the transport controls (Back /
 * Next / play) below the fold: a diagram you must scroll away from to advance. Fitting wins.
 * 1440x900 gives ~15px, 1920x1080 ~19px, and anything larger tops out at the 1600px modal cap.
 *
 * overflow-y-auto stays on as the safety net for the cases the cap cannot solve: the vertical
 * layout, and very short windows.
 */

import { useRef, useState, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { PlayCircle } from 'lucide-react'
import { Button, type ButtonProps } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { SchedulerConnectionDiagram, type DiagramNav } from './SchedulerConnectionDiagram'

export interface SchedulerConnectionDiagramDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Open at one of the twelve step points (1-12). Omit to mount on the frozen recap tableau. */
    initialStep?: number
    /**
     * Node hotspots exist only when this is supplied. Both host screens omit it: the diagram is
     * teaching how the screens relate, and navigating away from underneath an open modal is a
     * worse experience than a diagram you simply read.
     */
    onNavigate?: (dest: DiagramNav, index?: number) => void
    /**
     * The control focus returns to when the modal closes. SUPPLY THIS — see onCloseAutoFocus
     * below; without it focus lands on <body> and a keyboard user is dropped at the top of the
     * document.
     */
    // `| null` matters: @types/react 19 types useRef<T>(null) as RefObject<T | null>, so a plain
    // RefObject<HTMLElement> would reject every ref a caller can actually create.
    returnFocusRef?: RefObject<HTMLElement | null>
}

export function SchedulerConnectionDiagramDialog({
    open,
    onOpenChange,
    initialStep,
    onNavigate,
    returnFocusRef,
}: SchedulerConnectionDiagramDialogProps) {
    const { t } = useTranslation('schedulerDiagram')

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* No explicit aria-describedby / id here on purpose. Radix generates the pair and
                wires DialogTitle and DialogDescription itself; supplying our own id on the
                description leaves Radix's generated id pointing at nothing and it logs
                "Missing `Description` or `aria-describedby`" in dev. Verified in the browser. */}
            <DialogContent
                className="flex max-h-[92vh] w-[95vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1600px]"
                /**
                 * Focus restore has to be done by hand here, and this is not belt-and-braces.
                 *
                 * Radix's modal content already preventDefaults its own close-auto-focus and
                 * focuses `triggerRef` instead — but triggerRef is only populated by
                 * <DialogTrigger>. A dialog driven by a controlled `open` flag, as this one is,
                 * has no trigger, so Radix cancels the restore and then focuses nothing: on close
                 * activeElement becomes <body>, and a keyboard user who pressed Escape is
                 * returned to the top of the document rather than to the button they came from.
                 * Measured in the browser, not assumed — confirmed both before and after this fix.
                 *
                 * (The same hole exists in other controlled dialogs in this codebase. Out of
                 * scope here, but it is the same two lines wherever it is fixed.)
                 */
                onCloseAutoFocus={(event) => {
                    const el = returnFocusRef?.current
                    if (!el) return // no target given: leave Radix's behaviour alone
                    event.preventDefault()
                    el.focus()
                }}
            >
                {/* Padding tightens below sm. On a phone the modal is ~340px wide and the diagram
                    has already switched to its vertical layout; every pixel of gutter is a pixel
                    off an already 0.36-scaled canvas. */}
                <DialogHeader className="px-3 pb-3 pt-6 text-left sm:px-6">
                    <DialogTitle>{t('diagram.title', { defaultValue: 'Connecting a scheduler' })}</DialogTitle>
                    <DialogDescription>
                        {t('caption.open', {
                            defaultValue: 'Two tokens travel the same hop — and they do different jobs.',
                        })}
                    </DialogDescription>
                </DialogHeader>

                <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-6 sm:px-6">
                    <div
                        className="mx-auto w-full"
                        style={{ maxWidth: 'max(720px, calc((92vh - 150px) * 1.7778))' }}
                    >
                        {/* Radix unmounts dialog content on close, so every open is a fresh mount:
                            the clock, the step index and the idle play affordance all reset with
                            no teardown of our own. */}
                        {/* autoStart: opening this modal is itself the request to be shown, so it
                            begins at the beginning and plays rather than sitting on the recap.
                            A deep-linked step still wins, and reduced motion still never animates. */}
                        <SchedulerConnectionDiagram initialStep={initialStep} autoStart onNavigate={onNavigate} />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export interface ShowMeSchedulerDiagramButtonProps {
    /** Defaults to `outline` so it stays subordinate to the screen's primary Add/Create button. */
    variant?: ButtonProps['variant']
    size?: ButtonProps['size']
    className?: string
}

/**
 * The label is a literal, and that is a decision rather than an oversight.
 *
 * Both host screens are hardcoded English throughout — "Schedulers", "Manage on-premises and
 * cloud schedulers", "Add Scheduler", "Create Vault", every empty state. Running this one button
 * through i18n puts a German "Zeigen" immediately beside an English "Add Scheduler", which reads
 * worse than an English row (seen side by side in the browser, in de). The diagram's own copy IS
 * translated — that is where localisation earns its keep.
 *
 * The key exists and is ready: schedulerDiagram `ui.showMe`, en/de/fr. When the host screens are
 * localised, this becomes `const { t } = useTranslation('schedulerDiagram')` and
 * `{t('ui.showMe')}`. Note the de/fr values were placeholders written during the build and want
 * a translator's eye before they ship.
 */
const SHOW_ME_LABEL = 'Show Me'

export function ShowMeSchedulerDiagramButton({
    variant = 'outline',
    size,
    className,
}: ShowMeSchedulerDiagramButtonProps) {
    const [open, setOpen] = useState(false)
    const triggerRef = useRef<HTMLButtonElement>(null)

    return (
        <>
            <Button
                ref={triggerRef}
                variant={variant}
                size={size}
                className={className}
                onClick={() => setOpen(true)}
            >
                <PlayCircle className="mr-2 h-4 w-4" aria-hidden />
                {SHOW_ME_LABEL}
            </Button>
            <SchedulerConnectionDiagramDialog
                open={open}
                onOpenChange={setOpen}
                returnFocusRef={triggerRef}
            />
        </>
    )
}

export default ShowMeSchedulerDiagramButton
